// TextBots.jsx — Phase 5: AI Text Bots list page
// Cards: name, scripts count, examples count, feedback count, autoReply toggle.
// "+ New Text Bot" creates a bot via modal.

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare, Plus, ChevronRight, BookOpen, ThumbsUp,
  Zap, ZapOff, Lock, UserCheck, Loader2, FileText, RefreshCw,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet, apiPost, apiPut } from '../lib/api.js';
import { getPendingCount } from '../api/textBotSync.js';
import TextBotSyncModal from '../components/TextBotSyncModal.jsx';
import TopBar from '../components/TopBar.jsx';

// ─── New Bot Modal ────────────────────────────────────────────────────────────
function NewBotModal({ onClose, onCreated }) {
  const [name, setName]     = useState('');
  const [desc, setDesc]     = useState('');
  const [loading, setLoading] = useState(false);

  async function handleCreate(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      const res = await apiPost('/text-bots', { name: name.trim(), description: desc.trim() });
      if (res?.ok) {
        onCreated(res.bot);
        toast.success('Text bot created');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to create bot');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="panel w-full max-w-md p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-xl bg-civix-purple/20 flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-civix-purple" />
          </div>
          <h2 className="text-lg font-semibold text-white">New Text Bot</h2>
        </div>
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-xs text-white/50 mb-1">Bot Name *</label>
            <input
              className="input w-full"
              placeholder="e.g. Follow-Up Bot, Booking Confirmation Bot"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1">Description (optional)</label>
            <input
              className="input w-full"
              placeholder="Brief description of what this bot does"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1">
              Cancel
            </button>
            <button type="submit" disabled={loading || !name.trim()} className="btn-primary flex-1">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Bot'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── BotCard ──────────────────────────────────────────────────────────────────
function BotCard({ bot, onOpen, onToggleAutoReply }) {
  const isOwner = bot._isOwner !== false;

  return (
    <div
      onClick={() => onOpen(bot._id)}
      className="panel p-5 cursor-pointer hover:bg-white/5 transition-colors group relative"
    >
      {/* Shared banner */}
      {!isOwner && (
        <div className="absolute top-0 left-0 right-0 flex items-center gap-1.5 bg-civix-cyan/10 border-b border-civix-cyan/20 px-3 py-1.5 rounded-t-xl">
          <UserCheck className="w-3 h-3 text-civix-cyan shrink-0" />
          <span className="text-xs text-civix-cyan font-medium">Shared (use only)</span>
        </div>
      )}

      {/* Header */}
      <div className={`flex items-start justify-between gap-3 mb-4 ${!isOwner ? 'mt-7' : ''}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-civix-purple/20 flex items-center justify-center shrink-0">
            <MessageSquare className="w-5 h-5 text-civix-purple" />
          </div>
          <div>
            <div className="font-semibold text-white leading-tight">{bot.name}</div>
            {bot.description && (
              <div className="text-xs text-white/40 mt-0.5 line-clamp-1">{bot.description}</div>
            )}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-white/50 shrink-0 mt-1 transition-colors" />
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <Stat icon={FileText}   label="Scripts"  value={bot.scripts?.length || 0}          color="text-civix-violet" />
        <Stat icon={BookOpen}   label="Examples" value={bot.responseExamples?.length || 0} color="text-civix-cyan"   />
        <Stat icon={ThumbsUp}   label="Feedback" value={bot.feedback?.length || 0}          color="text-yellow-400"  />
      </div>

      {/* Footer: auto-reply toggle */}
      <div
        className="flex items-center justify-between pt-3 border-t border-civix-border/40"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-xs text-white/50 flex items-center gap-1.5">
          {bot.autoReply
            ? <Zap className="w-3.5 h-3.5 text-civix-cyan" />
            : <ZapOff className="w-3.5 h-3.5 text-white/30" />}
          Auto-reply {bot.autoReply ? 'ON' : 'OFF'}
        </span>
        {isOwner ? (
          <button
            onClick={() => onToggleAutoReply(bot)}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-hidden ${
              bot.autoReply ? 'bg-civix-cyan' : 'bg-civix-border'
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                bot.autoReply ? 'translate-x-4.5' : 'translate-x-0.5'
              }`}
            />
          </button>
        ) : (
          <span className="text-xs text-white/30 flex items-center gap-1">
            <Lock className="w-3 h-3" /> Read only
          </span>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-civix-panelLight rounded-lg p-2.5 text-center">
      <div className={`flex items-center justify-center gap-1 ${color} mb-1`}>
        <Icon className="w-3.5 h-3.5" />
        <span className="text-sm font-semibold">{value}</span>
      </div>
      <div className="text-[10px] text-white/40">{label}</div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function TextBots() {
  const nav = useNavigate();
  const qc  = useQueryClient();
  const [showNew, setShowNew]   = useState(false);
  const [showSync, setShowSync] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['text-bots'],
    queryFn:  () => apiGet('/text-bots'),
  });

  const { data: pendingData } = useQuery({
    queryKey: ['textbot-sync', 'pending-count'],
    queryFn:  () => getPendingCount(),
  });
  const pendingCount = pendingData?.data?.count || 0;

  const toggleAutoReply = useMutation({
    mutationFn: (bot) =>
      apiPut(`/text-bots/${bot._id}`, { autoReply: !bot.autoReply }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['text-bots'] });
      toast.success('Auto-reply updated');
    },
    onError: (err) => toast.error(err.message || 'Failed to update'),
  });

  const bots = data?.bots || [];

  function handleCreated(bot) {
    setShowNew(false);
    qc.invalidateQueries({ queryKey: ['text-bots'] });
    nav(`/text-bots/${bot._id}`);
  }

  return (
    <div className="space-y-6">
      <TopBar
        title="Text Bots"
        subtitle="AI-powered SMS response bots — train with scripts and examples, test and fine-tune with feedback."
        right={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSync(true)}
              className="btn-ghost flex items-center gap-2 relative"
            >
              <RefreshCw className="w-4 h-4" />
              Sync Bots
              {pendingCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-civix-cyan text-[10px] font-semibold text-black flex items-center justify-center">
                  {pendingCount}
                </span>
              )}
            </button>
            <button onClick={() => setShowNew(true)} className="btn-primary flex items-center gap-2">
              <Plus className="w-4 h-4" />
              New Text Bot
            </button>
          </div>
        }
      />

      {isLoading ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-6 h-6 text-civix-purple animate-spin" />
        </div>
      ) : bots.length === 0 ? (
        <div className="panel p-12 text-center">
          <MessageSquare className="w-12 h-12 text-civix-purple/40 mx-auto mb-4" />
          <div className="text-white/60 text-sm mb-4">No text bots yet.</div>
          <button onClick={() => setShowNew(true)} className="btn-primary mx-auto flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Create your first Text Bot
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {bots.map((bot) => (
            <BotCard
              key={bot._id}
              bot={bot}
              onOpen={(id) => nav(`/text-bots/${id}`)}
              onToggleAutoReply={(b) => toggleAutoReply.mutate(b)}
            />
          ))}
        </div>
      )}

      {showNew && (
        <NewBotModal
          onClose={() => setShowNew(false)}
          onCreated={handleCreated}
        />
      )}

      {showSync && <TextBotSyncModal onClose={() => setShowSync(false)} />}
    </div>
  );
}
